import React from 'react'
import { Link } from 'react-router-dom'
import { useI18nContext } from '../hooks/I18nContext'

const Start: React.FC = () => {
  const { t } = useI18nContext()

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-blue-50 to-green-50">
      <div className="relative z-10">
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-6">
                {t('start.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('start.hero.subtitle')}
              </p>
              <div className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
                <div className="aspect-video bg-gray-100 rounded-xl mb-6 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-pink-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-gray-600">{t('start.videos.title')}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Product Introduction */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('start.product.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <p className="text-lg text-gray-600 mb-6">
                    {t('start.product.description')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center mr-4">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{t('start.features.design')}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{t('start.features.safety')}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{t('start.features.ai')}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="aspect-square bg-gradient-to-br from-pink-200 to-blue-200 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                        <svg className="w-12 h-12 text-pink-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-600">{t('start.showcase.title')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Setup Guide */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('start.setup.title')}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="text-center">
                    <div className="w-16 h-16 bg-pink-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{step}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {t(`start.setup.step${step}.title`)}
                    </h3>
                    <p className="text-gray-600">
                      {t(`start.setup.step${step}.description`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Video Tutorials */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('start.videos.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { title: t('start.videoList.intro'), duration: '2:30' },
                  { title: t('start.videoList.setup'), duration: '5:15' },
                  { title: t('start.videoList.features'), duration: '3:45' }
                ].map((video, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                    <div className="aspect-video bg-gray-100 rounded-xl mb-4 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-pink-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-600">{video.duration}</p>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {video.title}
                    </h3>
                    <button className="w-full bg-pink-500 text-white py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors">
                      {t('start.watchVideo')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('start.faq.title')}
              </h2>
              <div className="space-y-6">
                {[
                  {
                    question: t('start.faqItems.charge.question'),
                    answer: t('start.faqItems.charge.answer')
                  },
                  {
                    question: t('start.faqItems.age.question'),
                    answer: t('start.faqItems.age.answer')
                  },
                  {
                    question: t('start.faqItems.setup.question'),
                    answer: t('start.faqItems.setup.answer')
                  }
                ].map((faq, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                {t('start.cta.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('start.cta.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/buy"
                  className="bg-pink-500 text-white py-3 px-8 rounded-lg text-lg font-semibold hover:bg-pink-600 transition-colors"
                >
                  {t('start.cta.buy')}
                </Link>
                <Link
                  to="/learning"
                  className="bg-white text-pink-500 py-3 px-8 rounded-lg text-lg font-semibold border-2 border-pink-500 hover:bg-pink-50 transition-colors"
                >
                  {t('start.cta.learnMore')}
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default Start
